import React from "react";
import { Button } from "@/components/ui/button";
import { Download, Calendar, Phone } from "lucide-react";
import { OptimizedImage } from "@/components/OptimizedImage";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/data/translations";
import { trackContactAction } from "@/utils/contactTracking";

// Combined images from rooms and restaurant galleries
const backgroundImages = [
  // Room images
  "/images/Homepage Shortlisted media/Gallery 01 Rooms/01 Family2.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 01 Rooms/Family balcony.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 01 Rooms/21 Junior suite1.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 01 Rooms/Copy of Junior suite S Balcony.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 01 Rooms/triple room.jpeg",
  "/images/Homepage Shortlisted media/Gallery 01 Rooms/15 Double Queen Balcony 1.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 01 Rooms/14 Double Queen Balcony 4.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 01 Rooms/Standard single.jpeg",
  "/images/Homepage Shortlisted media/Gallery 01 Rooms/a_view_of_the_mountains_from_the_porch_of_a_cabin__168.optimized.webp",
  // Restaurant images
  "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5196.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5363.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5140.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_6766.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_6394.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5178.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5265.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5195.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5403.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5177.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5192.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_5263.optimized.webp",
  "/images/Homepage Shortlisted media/Gallery 02 Restaurant-Food/IMG_6713.optimized.webp",
];

const CTASection = () => {
  const { language } = useLanguage();

  return (
    <section
      className="py-16 text-white relative overflow-hidden"
      data-oid="lv.xu43"
    >
      {/* Background image grid */}
      <div className="absolute inset-0" data-oid="dxdk9-j">
        {/* Desktop: 5 columns x 4 rows grid */}
        <div
          className="hidden lg:grid grid-cols-5 grid-rows-4 h-full"
          data-oid="lnie7rv"
        >
          {backgroundImages.slice(0, 20).map((image, index) => (
            <div
              key={index}
              className="relative overflow-hidden"
              data-oid="9qveph4"
            >
              <OptimizedImage
                src={image}
                alt={`Gallery image ${index + 1}`}
                className="absolute inset-0 w-full h-full object-cover opacity-85 hover:opacity-95 transition-opacity duration-300"
                priority={false}
                data-oid="73qhm56"
              />

              {/* First dark overlay for better text readability */}
              <div
                className="absolute inset-0 bg-black/40"
                data-oid="__2s5p_"
              />

              {/* Second dark overlay for additional depth */}
              <div
                className="absolute inset-0 bg-black/20"
                data-oid="jfgpn7r"
              />
            </div>
          ))}
        </div>

        {/* Mobile: 2 columns grid */}
        <div className="lg:hidden grid grid-cols-2 h-full" data-oid="9r1rffv">
          {backgroundImages.slice(0, 16).map((image, index) => (
            <div
              key={index}
              className="relative overflow-hidden"
              data-oid="1zojo8q"
            >
              <OptimizedImage
                src={image}
                alt={`Gallery image ${index + 1}`}
                className="absolute inset-0 w-full h-full object-cover opacity-85 hover:opacity-95 transition-opacity duration-300"
                priority={false}
                data-oid="9kagntc"
              />

              {/* Dark overlay for better text readability */}
              <div
                className="absolute inset-0 bg-black/50"
                data-oid="z9grc2o"
              />
            </div>
          ))}
        </div>

        {/* Gradient overlay for better text contrast */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-alpine-gold/40 to-lake-blue/40"
          data-oid="y0zv2gd"
        />
      </div>

      <div className="container mx-auto px-6 relative z-10" data-oid="54h2h8s">
        <div className="text-center max-w-4xl mx-auto" data-oid="gz787mp">
          <h2
            className="text-3xl md:text-4xl font-playfair mb-6"
            data-oid="0lv_93v"
          >
            {getTranslation(language, "localAttractions.cta.title")}
          </h2>

          <p
            className="text-xl md:text-2xl mb-8 text-white font-lato leading-relaxed"
            data-oid="21.ig1g"
          >
            {getTranslation(language, "localAttractions.cta.description")}
          </p>

          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10"
            data-oid="be6h20."
          >
            <div
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
              data-oid="xs0ibbf"
            >
              <div
                className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4"
                data-oid="dtp2_qk"
              >
                <Calendar className="w-6 h-6" data-oid="f4f.fku" />
              </div>
              <h3 className="font-semibold text-lg mb-2" data-oid="79t0:iv">
                {getTranslation(language, "localAttractions.cta.bookYourStay")}
              </h3>
              <p className="text-sm text-white/80" data-oid="el.ras9">
                {getTranslation(
                  language,
                  "localAttractions.cta.bookYourStayDesc",
                )}
              </p>
            </div>

            <div
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
              data-oid="49--41p"
            >
              <div
                className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4"
                data-oid="dkml1u4"
              >
                <Phone className="w-6 h-6" data-oid="4pe8:5u" />
              </div>
              <h3 className="font-semibold text-lg mb-2" data-oid="i4_3azo">
                {getTranslation(language, "localAttractions.cta.speakToExpert")}
              </h3>
              <p className="text-sm text-white/80" data-oid="z7u421o">
                {getTranslation(
                  language,
                  "localAttractions.cta.speakToExpertDesc",
                )}
              </p>
            </div>
          </div>

          <div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            data-oid="5zvch-."
          >
            <Button
              asChild
              size="lg"
              className="bg-white text-alpine-gold hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              trackingType="booking"
              trackingLabel={getTranslation(
                language,
                "localAttractions.cta.bookYourStay",
              )}
              trackingLocation="Local Attractions CTA"
              data-oid="50004rt"
            >
              <a
                href={import.meta.env.VITE_BOOKING_URL}
                data-oid="-u__pet"
              >
                <Calendar className="w-5 h-5 mr-2" data-oid="2ut9v4j" />
                {getTranslation(language, "localAttractions.cta.bookYourStay")}
              </a>
            </Button>
          </div>

          <div className="mt-8 text-center" data-oid="4yo5jtg">
            <p className="text-white/80 text-sm" data-oid="fipbha1">
              {getTranslation(language, "localAttractions.cta.questions")}{" "}
              <a
                href="tel:+41338410021"
                onClick={() =>
                  trackContactAction("concierge", "phone", {
                    phone_number: "+41338410021",
                    contact_source: "local_attractions_direct",
                    location: "local_attractions_cta",
                  })
                }
                className="contact-action-track font-semibold hover:underline"
                data-oid="1y9gx9o"
              >
                +41 33 841 00 21
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <div
        className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent"
        data-oid="05obwzd"
      ></div>
      <div
        className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent"
        data-oid="bs0:tld"
      ></div>
    </section>
  );
};

export default CTASection;
